import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Sparkles, Clock, Car, Users, HeartHandshake, Award, Route, ArrowUpRight,
} from "lucide-react";

/**
 * About — brand story + promise for Plenty Rydez.
 * Public route: /about
 */
export default function About() {
  return (
    <>
      {/* HERO */}
      <section
        data-testid="about-hero"
        className="relative overflow-hidden noise-overlay"
      >
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 20% 20%, rgba(57,214,255,0.25), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(22,179,221,0.2), transparent 55%)",
          }}
        />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 pt-24 pb-16 sm:pt-32 sm:pb-24 text-center">
          <div className="label-caps text-cyan text-[11px] tracking-[0.4em] mb-4 inline-flex items-center gap-2">
            <Sparkles size={12} /> About Plenty Rydez
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05] mb-6">
            Luxury in motion.<br />
            <span className="text-cyan">Excellence</span> without limits.
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Founded on a single vision — to elevate the standard of luxury
            transportation through exceptional service, premium vehicles, and an
            unwavering commitment to our clients.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16 space-y-8 text-white/80 leading-relaxed">
        <p>
          Serving <span className="text-white font-medium">New York, New Jersey,
          Pennsylvania</span> and destinations beyond, Plenty Rydez provides
          first-class chauffeur services and luxury vehicle rentals designed for
          individuals, families, executives, businesses, and special events.
          Whether you&apos;re traveling to the airport, attending a corporate meeting,
          celebrating a wedding, arriving at a red-carpet event, filming a music
          video, or simply enjoying a night out — we deliver an experience that
          is dependable, refined, and unforgettable.
        </p>
        <p>
          We believe luxury is more than the vehicle you ride in — it&apos;s how
          you&apos;re treated from beginning to end. Every reservation is handled with
          professionalism, discretion, punctuality, and attention to detail. Our
          team is dedicated to making every journey seamless, comfortable, and
          personalized to your needs.
        </p>
        <p>
          Our diverse fleet includes executive sedans, luxury SUVs, premium
          Sprinter vans, and specialty vehicles that allow us to accommodate
          everything from private travel to group transportation. In addition to
          our chauffeured services, Plenty Rydez offers select luxury rental
          vehicles, giving clients the flexibility to drive themselves while
          still enjoying the quality and prestige our brand represents.
        </p>

        {/* Pull-quote */}
        <blockquote
          data-testid="about-quote"
          className="my-14 relative pl-6 sm:pl-10 border-l-2 border-cyan/60"
        >
          <div className="font-serif italic text-2xl sm:text-3xl leading-snug text-white">
            &ldquo;We don&apos;t believe in one-size-fits-all service. Every client
            deserves a transportation solution tailored to their occasion,
            schedule, and expectations.&rdquo;
          </div>
        </blockquote>

        <p>
          What truly separates Plenty Rydez from other transportation companies
          is our commitment to creating <span className="text-cyan">experiences</span> —
          not just rides. From the first phone call to the final destination,
          our focus remains on delivering excellence with every mile.
        </p>
        <p>
          As our company continues to grow, so does our commitment to the
          communities we serve. Through charitable initiatives, local
          partnerships, and community events, we strive to make a positive
          impact beyond transportation — because success means more when it&apos;s
          shared.
        </p>
        <p>
          At Plenty Rydez, we measure our success by the trust our clients place
          in us and the relationships we build along the way. Every trip is an
          opportunity to exceed expectations, and every customer is treated with
          the professionalism, respect, and care they deserve.
        </p>
        <p className="text-white text-lg font-serif italic">
          Plenty Rydez isn&apos;t just about getting you from one destination to
          another. It&apos;s about how you arrive, how you&apos;re treated, and the
          lasting impression we leave long after the journey ends.
        </p>
      </section>

      {/* SERVICE PILLARS */}
      <section
        data-testid="about-pillars"
        className="mx-auto max-w-7xl px-5 sm:px-8 py-16"
      >
        <div className="text-center mb-12">
          <div className="label-caps text-cyan text-[11px] tracking-[0.3em] mb-3">
            Where we shine
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">Built for every occasion</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Route, title: "Airport transfers", body: "JFK, LGA, EWR — meet & greet, luggage, quiet arrivals." },
            { icon: Award, title: "Corporate & executive", body: "Multi-stop roadshows, board-ready, discreet." },
            { icon: HeartHandshake, title: "Weddings & red carpet", body: "Fleet coordination, on-time, on-brand." },
            { icon: Users, title: "Groups & families", body: "Luxury Sprinters and SUVs for parties big or small." },
          ].map(({ icon: I, title, body }) => (
            <div
              key={title}
              className="glass rounded-2xl p-6 border border-white/5 hover:border-cyan/40 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-cyan/15 text-cyan flex items-center justify-center mb-4">
                <I size={20} />
              </div>
              <div className="font-serif text-xl mb-1.5">{title}</div>
              <p className="text-sm text-white/60 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OUR PROMISE */}
      <section
        data-testid="about-promise"
        className="mx-auto max-w-6xl px-5 sm:px-8 py-16"
      >
        <div className="glass rounded-3xl p-8 sm:p-12 border border-cyan/20 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl"
            style={{ background: "#39D6FF" }}
          />
          <div className="relative z-10 grid md:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="label-caps text-cyan text-[11px] mb-3">Our promise</div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-4">
                Six commitments.<br />
                Zero compromises.
              </h2>
              <p className="text-white/60 text-sm">
                What every client — first-time or repeat — can count on, every single mile.
              </p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Sparkles, label: "Luxury without compromise" },
                { icon: ShieldCheck, label: "Professional, courteous chauffeurs" },
                { icon: Car, label: "Modern, meticulously maintained vehicles" },
                { icon: Clock, label: "Reliable, on-time service" },
                { icon: Users, label: "Personalized transportation experiences" },
                { icon: HeartHandshake, label: "Safety, discretion, and comfort in every mile" },
              ].map(({ icon: I, label }) => (
                <li
                  key={label}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/5"
                >
                  <div className="w-8 h-8 rounded-lg bg-cyan/15 text-cyan flex-shrink-0 flex items-center justify-center">
                    <I size={14} />
                  </div>
                  <span className="text-sm text-white/85 leading-snug">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-20 text-center">
        <div className="font-serif italic text-2xl sm:text-3xl text-white/90 leading-snug mb-3">
          Plenty Rydez
        </div>
        <div className="label-caps text-cyan text-xs tracking-[0.4em] mb-10">
          Luxury in motion · Excellence without limits
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/fleet"
            data-testid="about-cta-fleet"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan text-midnight text-sm font-medium hover:brightness-110 transition"
          >
            Explore the fleet <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/support"
            data-testid="about-cta-contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-sm text-white/80 hover:text-white hover:border-white/40 transition"
          >
            Talk to concierge
          </Link>
        </div>
      </section>
    </>
  );
}
